import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { fmtNum } from "../lib/format";
import { toast } from "sonner";
import {
  MagnifyingGlass, X, ArrowClockwise, CircleNotch, CaretUp, CaretDown,
  CloudArrowDown, MapPin, Warning, Question,
} from "@phosphor-icons/react";

// Column catalogue — order = display order. type drives sort.
const COLS = [
  { key: "item_code", label: "Item Code", type: "text" },
  { key: "item_name", label: "Item Name", type: "text" },
  { key: "primary_site", label: "Primary Site", type: "site" },
  { key: "other_sites", label: "Other Sites", type: "sites" },
  { key: "on_hand_total", label: "On-hand", type: "num" },
  { key: "basis", label: "Basis", type: "basis" },
  { key: "confidence", label: "Confidence", type: "conf" },
  { key: "ship_from", label: "Ship-from history", type: "ship" },
];
const COL_BY_KEY = Object.fromEntries(COLS.map((c) => [c.key, c]));

// Stable colour per SAP site code so P1/P2/P9… read consistently across the grid.
const SITE_COLORS = {
  P1: "#2A5C9B", P2: "#0F766E", P2W: "#0E7490", P3: "#B45309",
  P4: "#7C3AED", P5: "#BE123C", P6: "#4D7C0F", P7: "#9333EA",
  P8: "#0369A1", P9: "#C2410C",
};
const siteColor = (s) => SITE_COLORS[s] || "#64748B";

// "2025-07-31" -> "Jul 31, 2025" for the shipped-since toggle label.
const fmtShippedSince = (iso) => {
  if (!iso) return "";
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return isNaN(d) ? iso : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const BASIS_LABEL = { stock: "In stock", history: "Shipped from", both: "Stock + shipped", unknown: "No data" };
const CONF = {
  high: { label: "High", bg: "#DCFCE7", fg: "#166534" },
  review: { label: "Review — multi-site", bg: "#FEF3C7", fg: "#92400E" },
  medium: { label: "Medium", bg: "#DBEAFE", fg: "#1E40AF" },
  unknown: { label: "Unknown", bg: "#F1F5F9", fg: "#475569" },
};

const SiteBadge = ({ s }) => (
  <span className="ism-badge" style={{ background: siteColor(s) }} data-testid={`ism-site-${s}`}>{s}</span>
);

export default function ItemSiteMap() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [siteFilter, setSiteFilter] = useState("");
  const [flag, setFlag] = useState("");                 // "" | "multi" | "unknown"
  const [sort, setSort] = useState({ key: "item_code", dir: "asc" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/item-site-map");
      if (!data?.ok) { toast.error("Could not load the map"); setRows([]); }
      else { setRows(data.rows || []); setMeta(data.meta || null); setSites(data.sites || []); }
    } catch {
      toast.error("Failed to reach the item-site-map service");
      setRows([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/item-site-map/refresh");
      if (!data?.ok) toast.error(`Refresh failed: ${data?.error || "unknown error"}`);
      else { toast.success(`Rebuilt from SAP · ${data.item_count} items in ${data.duration_s}s`); await load(); }
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "You don't have access to refresh" : "Refresh failed");
    } finally { setRefreshing(false); }
  };

  const toggleSort = (k) =>
    setSort((s) => (s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" }));

  const view = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (siteFilter && !(r.sites_list || []).includes(siteFilter)) return false;
      if (flag === "multi" && !r.multi_site) return false;
      if (flag === "unknown" && r.site_known) return false;
      if (flag === "shipped" && !r.shipped_recently) return false;
      if (q && !(`${r.item_code} ${r.item_name} ${(r.item_numbers || []).join(" ")}`.toLowerCase().includes(q))) return false;
      return true;
    });
    const dir = sort.dir === "asc" ? 1 : -1;
    out = [...out].sort((a, b) => {
      if (sort.key === "on_hand_total") return ((a.on_hand_total || 0) - (b.on_hand_total || 0)) * dir;
      const av = String(a[sort.key] ?? ""), bv = String(b[sort.key] ?? "");
      return av.localeCompare(bv) * dir;
    });
    return out;
  }, [rows, search, siteFilter, flag, sort]);

  const exportCsv = () => {
    if (!view.length) { toast.error("Nothing to export for the current filters"); return; }
    const head = ["Item Code", "Item Numbers", "Item Name", "Primary Site", "Other Sites", "On-hand", "UOM", "Basis", "Confidence", "Ship-from"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const body = view.map((r) => [
      r.item_code, (r.item_numbers || []).join(" / "), r.item_name, r.primary_site || "UNKNOWN",
      (r.other_sites || []).join(" / "), r.on_hand_total, r.uom,
      BASIS_LABEL[r.basis] || r.basis, (CONF[r.confidence] || {}).label || r.confidence,
      (r.ship_from || []).map((s) => `${s.site}×${s.count}${s.last_date ? ` (${s.last_date})` : ""}`).join(" / "),
    ].map(esc).join(",")).join("\n");
    const blob = new Blob([head.join(",") + "\n" + body], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `item-site-map-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const anyFilter = search || siteFilter || flag;
  const clearAll = () => { setSearch(""); setSiteFilter(""); setFlag(""); };

  const renderCell = (r, c) => {
    if (c.type === "site")
      return r.primary_site
        ? <SiteBadge s={r.primary_site} />
        : <span className="ism-unknown"><Question size={13} weight="bold" /> unknown</span>;
    if (c.type === "sites")
      return (r.other_sites || []).length
        ? <span className="ism-badges">{r.other_sites.map((s) => <SiteBadge key={s} s={s} />)}</span>
        : <span className="ism-dim">—</span>;
    if (c.type === "num") {
      const main = r.stock?.find((s) => s.site === r.primary_site);
      return <span className="mono" title={(r.stock || []).map((s) => `${s.site}: ${fmtNum(s.on_hand)}`).join("\n") || "no stock"}>
        {r.on_hand_total ? fmtNum(r.on_hand_total) : "0"} <span className="ism-dim">{r.uom}</span>
        {main && r.on_hand_total !== main.on_hand ? "" : ""}
      </span>;
    }
    if (c.type === "basis") return <span className="ism-basis">{BASIS_LABEL[r.basis] || r.basis}</span>;
    if (c.type === "conf") {
      const cf = CONF[r.confidence] || CONF.unknown;
      return <span className="ism-pill" style={{ background: cf.bg, color: cf.fg }} data-testid={`ism-conf-${r.confidence}`}>{cf.label}</span>;
    }
    if (c.type === "ship")
      return (r.ship_from || []).length
        ? <span className="ism-ship">{r.ship_from.map((s) => (
            <span key={s.site} className="ism-shipitem" title={s.last_date ? `last ${s.last_date}` : ""}>
              <b style={{ color: siteColor(s.site) }}>{s.site}</b>·{s.count}
            </span>))}</span>
        : <span className="ism-dim">—</span>;
    if (c.key === "item_code")
      return (
        <span className="ism-code">
          <b>{r.item_code}</b>
          {(r.item_numbers || []).length ? <span className="ism-itemno">#{r.item_numbers.join(", ")}</span> : null}
        </span>
      );
    return r[c.key] || "—";
  };

  return (
    <Layout breadcrumb={["Setup", "Item → Site Map"]}>
      <div className="ooa-wrap" data-testid="item-site-map-page">
        <div className="ooa-head">
          <div>
            <h1 className="ooa-title"><MapPin size={22} weight="duotone" /> Item → Site Map</h1>
            <div className="ooa-sub">
              Every item in your OMS purchase orders, mapped to its SAP site(s) · stock-by-site + ship-from history
              {meta?.last_built && <> · built {new Date(meta.last_built).toLocaleString()}</>}
              {meta?.item_count != null && <> · {meta.item_count} items</>}
            </div>
          </div>
          <div className="ooa-actions">
            <button data-testid="ism-export-btn" className="ooa-btn" onClick={exportCsv} disabled={!view.length}>
              <CloudArrowDown size={15} weight="bold" /> Export CSV
            </button>
            <button data-testid="ism-refresh-btn" className="ooa-btn primary" onClick={refresh} disabled={refreshing}>
              {refreshing ? <CircleNotch size={15} weight="bold" className="spin" /> : <ArrowClockwise size={15} weight="bold" />} Refresh from SAP
            </button>
          </div>
        </div>

        {meta && (
          <div className="ism-stats" data-testid="ism-stats">
            <span><b>{meta.item_count}</b> items</span>
            <span className="ism-stat-multi"><Warning size={13} weight="bold" /> <b>{meta.multi_site_count}</b> multi-site</span>
            <span className="ism-stat-unknown"><Question size={13} weight="bold" /> <b>{meta.unknown_count}</b> site unknown</span>
            {meta.shipped_recently_count != null && (
              <span className="ism-stat-shipped" title={meta.shipped_since ? `Posted dispatches since ${fmtShippedSince(meta.shipped_since)}` : ""}>
                <b>{meta.shipped_recently_count}</b> shipped since {fmtShippedSince(meta.shipped_since)}
              </span>
            )}
            {meta.primary_site_counts && Object.entries(meta.primary_site_counts).map(([s, n]) => (
              <span key={s} className="ism-stat-site"><SiteBadge s={s} /> {n}</span>
            ))}
          </div>
        )}

        <div className="ooa-toolbar">
          <div className="ooa-search">
            <MagnifyingGlass size={16} weight="bold" />
            <input data-testid="ism-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Find by item code or name…" />
            {search && <button className="ooa-clear" onClick={() => setSearch("")}><X size={13} weight="bold" /></button>}
          </div>
          <select data-testid="ism-filter-site" className="ooa-select" value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)}>
            <option value="">All sites</option>
            {sites.map((s) => <option key={s} value={s}>Site {s}</option>)}
          </select>
          <div className="ism-chips">
            <button data-testid="ism-chip-all" className={"ism-chip" + (flag === "" ? " on" : "")} onClick={() => setFlag("")}>All</button>
            <button data-testid="ism-chip-multi" className={"ism-chip" + (flag === "multi" ? " on" : "")} onClick={() => setFlag(flag === "multi" ? "" : "multi")}>Multi-site</button>
            <button data-testid="ism-chip-unknown" className={"ism-chip" + (flag === "unknown" ? " on" : "")} onClick={() => setFlag(flag === "unknown" ? "" : "unknown")}>Site unknown</button>
            {meta?.shipped_recently_count != null && (
              <button data-testid="ism-chip-shipped" className={"ism-chip" + (flag === "shipped" ? " on" : "")}
                onClick={() => setFlag(flag === "shipped" ? "" : "shipped")}
                title={meta?.shipped_since ? `Items on posted dispatches since ${fmtShippedSince(meta.shipped_since)}` : ""}>
                Shipped since {fmtShippedSince(meta?.shipped_since)}
              </button>
            )}
          </div>
          {anyFilter ? <button data-testid="ism-clear-filters" className="ooa-btn ghost" onClick={clearAll}><X size={13} weight="bold" /> Clear</button> : null}
          <div className="ooa-count" data-testid="ism-count">
            {view.length} item{view.length === 1 ? "" : "s"}
          </div>
        </div>

        <div className="ooa-tablewrap">
          <table className="ooa-table" data-testid="ism-table">
            <thead>
              <tr>
                {COLS.map((c) => (
                  <th key={c.key} data-testid={`ism-th-${c.key}`} onClick={() => toggleSort(c.key)}
                    className={c.type === "num" ? "num" : ""}>
                    <span className="ooa-th-inner">
                      {c.label}
                      {sort.key === c.key && (sort.dir === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan={COLS.length} className="ooa-empty"><CircleNotch size={18} weight="bold" className="spin" /> Building the map from SAP…</td></tr>
              )}
              {!loading && view.length === 0 && (
                <tr><td colSpan={COLS.length} className="ooa-empty">{rows.length ? "No items match the current filters." : "No map data yet — hit Refresh from SAP."}</td></tr>
              )}
              {view.map((r, i) => (
                <tr key={r.item_code} data-testid={`ism-row-${i}`}
                  className={!r.site_known ? "ism-row-unknown" : r.multi_site ? "ism-row-multi" : ""}>
                  {COLS.map((c) => (
                    <td key={c.key} className={c.type === "num" ? "num" : ""}>{renderCell(r, c)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
